import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Calendar, Ticket, User as UserIcon, QrCode } from "@phosphor-icons/react";
import { Link } from "react-router-dom";
import { supabase, offerStartsAt } from "../lib/supabase";
import { useAuth } from "../context/AuthContext";

const formatTime = (d) => d ? d.toLocaleString("es-ES", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" }) : "—";

const UserDashboard = () => {
  const { authUser, user } = useAuth();
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!authUser) return;
    (async () => {
      const { data } = await supabase
        .from("bookings")
        .select("*, offer:offers(*, business:businesses(*))")
        .eq("user_id", authUser.id)
        .order("created_at", { ascending: false });
      setBookings(data || []);
      setLoading(false);
    })();
  }, [authUser]);

  const upcoming = bookings.filter((b) => {
    const s = offerStartsAt(b.offer);
    return s && s > new Date();
  });

  return (
    <div className="max-w-[1400px] mx-auto px-6 md:px-12 lg:px-16 py-12 md:py-16" data-testid="user-dashboard">
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="mb-12">
        <span className="text-[11px] uppercase tracking-[0.3em] text-sage-deep block mb-3">/ mi cuenta</span>
        <h1 className="font-heading text-4xl md:text-5xl font-light text-forest">Hola, <span className="serif-italic font-medium text-sage-deep">{(authUser?.user_metadata?.full_name || authUser?.email || "").split(" ")[0]}</span></h1>
        <p className="text-stone mt-2">Aquí tienes tu actividad reciente.</p>
      </motion.div>

      <div className="grid md:grid-cols-3 gap-4 mb-12">
        <Stat icon={<Ticket size={20} />} l="Reservas" v={bookings.length} />
        <Stat icon={<Calendar size={20} />} l="Próximas" v={upcoming.length} />
        <Stat icon={<UserIcon size={20} />} l="Email" v={authUser?.email} />
      </div>

      <div className="space-y-3">
        <h2 className="text-xs uppercase tracking-[0.3em] text-sage-deep mb-4">/ Historial de reservas</h2>
        {loading ? (
          [...Array(3)].map((_, i) => <div key={i} className="h-24 bg-mist animate-pulse rounded-xl" />)
        ) : bookings.length === 0 ? (
          <div className="border border-dashed border-forest/15 rounded-2xl p-12 text-center">
            <p className="text-stone mb-4">Aún no tienes reservas. ¡Es buen momento para empezar!</p>
            <Link to="/actividades" className="bg-sage-deep text-paper px-6 py-3 uppercase tracking-wider text-sm font-medium inline-block rounded-full">Explorar ofertas</Link>
          </div>
        ) : (
          bookings.map((b) => {
            const s = offerStartsAt(b.offer);
            return (
              <div key={b.id} data-testid={`booking-${b.id}`} className="border border-forest/10 bg-white rounded-xl p-5 flex flex-col sm:flex-row sm:items-center gap-4 hover:border-sage/40 transition">
                <div className="flex-1">
                  <div className="font-heading font-semibold text-lg text-forest">{b.offer?.title || "—"}</div>
                  <div className="text-sm text-stone">{b.offer?.business?.name} · {formatTime(s)}</div>
                  {b.qr_token && (
                    <div className="mt-2 inline-flex items-center gap-2 text-[11px] font-mono bg-mist px-2 py-1 rounded text-stone">
                      <QrCode size={12} /> {b.qr_token.slice(0, 18)}…
                    </div>
                  )}
                </div>
                <div className="text-right">
                  <span className={`text-[10px] uppercase tracking-wider px-2 py-1 rounded-full ${
                    b.status === "confirmed" ? "bg-sage/20 text-sage-deep" :
                    b.status === "used" ? "bg-lavender/30 text-forest" :
                    b.status === "expired" ? "bg-ash/30 text-stone" : "bg-peach/30 text-forest"
                  }`}>{b.status}</span>
                </div>
              </div>
            );
          })
        )}
      </div>
    </div>
  );
};

const Stat = ({ icon, l, v }) => (
  <div className="border border-forest/10 bg-white rounded-2xl p-6">
    <div className="flex items-center gap-2 text-sage-deep mb-3">{icon}</div>
    <div className="text-[10px] uppercase tracking-[0.2em] text-stone">{l}</div>
    <div className="font-heading text-2xl font-semibold mt-1 truncate text-forest">{v ?? "—"}</div>
  </div>
);

export default UserDashboard;
